import isEmpty from "lodash/isEmpty";
import sortBy from "lodash/sortBy";
import { BookingFormMode } from "PFApp/booking/components/booking_form";
import { DetailsPanelType } from "PFApp/booking/components/details_panel/details_panel.types";
import { calculateActiveBookingIndexState } from "PFApp/booking/components/details_panel/hooks/use_active_booking_index.utils";
import { useEffect, useState } from "react";

import { useOpenedDetailsPanels } from "./use_opened_details_panels";

/**
 * It ensures the connection between Booking and BookingForm side panels in terms of the bookings carousel.
 * Assumption - both the side panels are one after another to maintain connection.
 * */
export const useActiveBookingIndex = () => {
  const openPanels = useOpenedDetailsPanels();
  const sortedOpenPanels = sortBy(openPanels, "index");

  const [activeBookingIndexByType, setActiveBookingIndexByType] = useState<
    Record<DetailsPanelType.Booking | DetailsPanelType.BookingForm, number | undefined>
  >({
    [DetailsPanelType.Booking]: undefined,
    [DetailsPanelType.BookingForm]: undefined
  });

  const bookingFormPanelIndex = sortedOpenPanels.findIndex(
    ({ data }) => data.type === DetailsPanelType.BookingForm
  );
  const bookingPanelIndex = sortedOpenPanels.findIndex(({ data }) => data.type === DetailsPanelType.Booking);
  const isBookingFormOpen = bookingFormPanelIndex !== -1;
  const isBookingPanelOpen = bookingPanelIndex !== -1;

  const bookingFormSidePanel = sortedOpenPanels[bookingFormPanelIndex];
  const { props: bookingFormProps } = bookingFormSidePanel?.data.bookingFormDetails ?? {};

  // on open/close new panels, active index of new open panel needs to be updated basing on the existing panel
  useEffect(() => {
    const result = calculateActiveBookingIndexState({
      bookingFormPanel: {
        order: bookingFormPanelIndex,
        isOpen: isBookingFormOpen,
        mode: bookingFormProps?.mode
      },
      bookingPanel: { order: bookingPanelIndex, isOpen: isBookingPanelOpen },
      activeBookingIndexByType
    });
    if (!isEmpty(result)) {
      setActiveBookingIndexByType((prevState) => ({
        ...prevState,
        ...result
      }));
    }
  }, [bookingFormPanelIndex, bookingPanelIndex]);

  // on manual index change the active index of the other panel is also updated
  const handleSetActiveBookingIndexId =
    (panelType: DetailsPanelType.Booking | DetailsPanelType.BookingForm) => (index: number | undefined) => {
      const isBookingFormOpenInEditAllMode = bookingFormProps?.mode === BookingFormMode.EditAll;
      const isBookingPanelOpen = !!openPanels.find(({ data }) => data.type === DetailsPanelType.Booking);
      const areBothPanelsOneAfterAnother = Math.abs(bookingFormPanelIndex - bookingPanelIndex) === 1;

      const relatedSidePanel =
        panelType === DetailsPanelType.Booking ? DetailsPanelType.BookingForm : DetailsPanelType.Booking;
      const shouldUpdateRelatedSidePanel =
        areBothPanelsOneAfterAnother &&
        ((panelType === DetailsPanelType.Booking && isBookingFormOpenInEditAllMode) ||
          (panelType === DetailsPanelType.BookingForm &&
            isBookingFormOpenInEditAllMode &&
            isBookingPanelOpen));

      setActiveBookingIndexByType((prevState) => ({
        ...prevState,
        [panelType]: index,
        ...(shouldUpdateRelatedSidePanel ? { [relatedSidePanel]: index } : {})
      }));
    };

  return {
    activeBookingIndexByType,
    handleSetActiveBookingIndexId
  };
};
